/**
 * src/components/assets/FinancialYearReview.jsx
 * Route: /assets/financial-year-review
 */
import { useState, useRef } from 'react';
import toast from 'react-hot-toast';

const API   = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const token = () => localStorage.getItem('iassets_token');

const Card = ({ children, style }) => (
  <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0',
    boxShadow:'0 1px 4px rgba(0,0,0,.06)', ...style }}>
    {children}
  </div>
);

export default function FinancialYearReview() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [file,      setFile]      = useState(null);
  const fileRef = useRef(null);

  /* ── Export ────────────────────────────────────────────── */
  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API}/financial/excel/export`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `iAssets_FY_Review_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('FY Review exported with live asset & FY reference data');
    } catch(e) { toast.error('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  /* ── Import ────────────────────────────────────────────── */
  const handleImport = async () => {
    if (!file) { toast.error('Select an Excel file first'); return; }
    setImporting(true); setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await fetch(`${API}/financial/excel/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || JSON.stringify(data));
      setResult(data);
      toast.success(`Import done — ${data.created} created, ${data.updated} updated`);
    } catch(e) { toast.error('Import failed: ' + e.message); }
    finally { setImporting(false); }
  };

  return (
    <div style={{ maxWidth:800, fontFamily:'-apple-system,sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:'#1a1f2e', margin:0 }}>
          📊 Financial Year Review — Import / Export
        </h1>
        <p style={{ fontSize:13, color:'#64748b', marginTop:5 }}>
          Export FY review data to Excel · Fill or update financial values · Re-import to sync database.
        </p>
      </div>

      {/* Field Reference */}
      <Card style={{ padding:'16px 20px', marginBottom:18,
        background:'#f0f6ff', border:'1px solid #bfdbfe' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#1a56db', marginBottom:10 }}>
          📑 Template Fields (per row = one Asset × one Financial Year)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          {[
            ['Asset Code *',                   'Unique asset code — must exist in database',           true],
            ['Asset Name',                     'Auto-filled from asset master on export',              false],
            ['Financial Year *',               'Format: YYYY-YY e.g. 2026-27',                        true],
            ['Acquisition & Production Cost',  'Total acquisition cost stored in DB',                  false],
            ['Opening Book Value',             'BV at start of FY — used to calculate NBV',           false],
            ['Planned Ordinary Depreciation',  'Planned dep amount for the FY (e.g. 2026-27)',        false],
            ['Posted Ordinary Depreciation',   'Actual posted dep for the FY',                        false],
            ['Net Book Value (Year End)',       'Auto-calculated: Opening BV − Planned Dep',           false],
          ].map(([k, v, req]) => (
            <div key={k} style={{ display:'flex', gap:8, padding:'6px 10px',
              background: req ? '#fff9e6' : '#fff', borderRadius:7,
              border:`1px solid ${req ? '#fde68a' : '#e2e8f0'}`, fontSize:12 }}>
              <span style={{ fontWeight:700, color: req ? '#dc2626' : '#1a56db', flexShrink:0 }}>
                {k}{req ? ' *' : ''}:
              </span>
              <span style={{ color:'#64748b' }}>{v}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Formula Note */}
      <Card style={{ padding:'12px 16px', marginBottom:18,
        background:'#f0fdf4', border:'1px solid #bbf7d0' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#16a34a', marginBottom:4 }}>
          🧮 Auto-Calculation on Import
        </div>
        <div style={{ fontSize:12, color:'#166534', fontFamily:'monospace',
          background:'#dcfce7', padding:'8px 12px', borderRadius:7,
          display:'inline-block' }}>
          Net Book Value (Year End) = Opening Book Value − Planned Ordinary Depreciation
        </div>
        <div style={{ fontSize:11, color:'#64748b', marginTop:6 }}>
          If "Net Book Value" is filled in the sheet, the imported value is still recalculated from Opening BV and Planned Dep for accuracy.
        </div>
      </Card>

      {/* Export + Import cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>

        {/* Export */}
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📤 Export FY Review
          </div>
          <p style={{ fontSize:12, color:'#64748b', lineHeight:1.6, marginBottom:16 }}>
            Downloads Excel with all existing FY review records + live asset list and financial year reference sheets pre-filled.
          </p>
          <button onClick={handleExport} disabled={exporting}
            style={{ width:'100%', padding:'12px', border:'none', borderRadius:9,
              background: exporting ? '#93c5fd' : '#1a56db', color:'#fff',
              fontSize:14, fontWeight:700, cursor: exporting ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export to Excel'}
          </button>
        </Card>

        {/* Import */}
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📥 Import FY Review
          </div>
          <p style={{ fontSize:12, color:'#64748b', lineHeight:1.6, marginBottom:10 }}>
            Upload filled Excel. Matches by <strong>Asset Code + Financial Year</strong>. Updates existing or creates new records.
          </p>
          <div onClick={() => fileRef.current?.click()}
            style={{ border:`2px dashed ${file ? '#1a56db' : '#e2e8f0'}`,
              borderRadius:9, padding:'14px', textAlign:'center',
              cursor:'pointer', marginBottom:10,
              background: file ? '#eff6ff' : '#f8fafc' }}>
            <input ref={fileRef} type="file" accept=".xlsx"
              style={{ display:'none' }}
              onChange={e => { setFile(e.target.files[0]); setResult(null); }}/>
            {file ? (
              <div>
                <div style={{ fontSize:20 }}>📄</div>
                <div style={{ fontSize:12, fontWeight:600, color:'#1a56db' }}>{file.name}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>{(file.size/1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:24 }}>📂</div>
                <div style={{ fontSize:12, color:'#64748b' }}>Click to select .xlsx file</div>
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {file && (
              <button onClick={() => { setFile(null); setResult(null); fileRef.current.value = ''; }}
                style={{ padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:8,
                  background:'#fff', fontSize:12, cursor:'pointer', color:'#64748b' }}>
                ✕
              </button>
            )}
            <button onClick={handleImport} disabled={importing || !file}
              style={{ flex:1, padding:'12px', border:'none', borderRadius:9,
                background: importing ? '#93c5fd' : !file ? '#e2e8f0' : '#16a34a',
                color: !file ? '#94a3b8' : '#fff',
                fontSize:14, fontWeight:700, cursor: (importing || !file) ? 'not-allowed' : 'pointer' }}>
              {importing ? '⏳ Importing…' : '⬆ Import Excel'}
            </button>
          </div>
        </Card>
      </div>

      {/* Import Result */}
      {result && (
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:14 }}>
            ✅ Import Result
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            {[
              { label:'Created', val:result.created, color:'#16a34a', bg:'#f0fdf4' },
              { label:'Updated', val:result.updated, color:'#1a56db', bg:'#eff6ff' },
              { label:'Skipped', val:result.skipped, color:'#f59e0b', bg:'#fefce8' },
              { label:'Total',   val:result.total_rows, color:'#374151', bg:'#f8fafc' },
            ].map(s => (
              <div key={s.label} style={{ background:s.bg, borderRadius:9, padding:'12px',
                textAlign:'center', border:`1px solid ${s.color}33` }}>
                <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.val}</div>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {result.errors?.length > 0 && (
            <div style={{ background:'#fff1f2', border:'1px solid #fecaca',
              borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:6 }}>
                ⚠ Errors ({result.errors.length})
              </div>
              <div style={{ fontSize:11, color:'#7f1d1d', fontFamily:'monospace',
                lineHeight:1.8, maxHeight:180, overflowY:'auto' }}>
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
